// Saved dashboard page — opened in a new tab from the "Your Dashboards"
// sidebar section (AiChat.jsx).
//
// Fixed, pre-built frontend page (a real React/Cloudscape page, not an
// AI-authored one) that reads a saved dashboard's title/widgets/filters
// from the backend and renders each widget via WidgetRenderer — same
// rendering path the live-build draft preview uses. Manual refresh only
// (locked decision #9, no auto-polling) — a Refresh button re-runs every
// widget's SQL with the current filter selection bound as Redshift Data
// API parameters (services/widget_query.py on the backend), never by
// rewriting SQL text.
//
// Permission model (locked decision #4): owner can edit filters, refresh,
// share, and delete. A viewer (shared with) can view and refresh, but
// cannot edit/share/delete — this page hides those affordances for
// non-owners; the backend independently re-enforces the same boundary
// server-side (routes/dashboards.py), since frontend hiding is never the
// real security boundary.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AppLayout, Box, Alert, Spinner, Modal, Button } from '@cloudscape-design/components';
import { dashboardsApi } from './dashboardsApi.js';
import DashboardSections from './DashboardSections.jsx';
import DashboardFilterBar from './DashboardFilterBar.jsx';
import ShareDashboardModal from './ShareDashboardModal.jsx';
import './ai-chat.css';

// Inline-editable title — click the pencil to enter edit mode, press
// Enter or blur to save. Owner-only; viewers see plain text.
function EditableTitle({ title, isOwner, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(title);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setValue(title); }, [title]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commit = async () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === title) {
      setValue(title);
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(trimmed);
      setEditing(false);
    } catch {
      // revert on failure
      setValue(title);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    if (e.key === 'Escape') { setValue(title); setEditing(false); }
  };

  if (!isOwner || !editing) {
    return (
      <span className="ai-chat-dashboard-title-wrapper">
        <span>{title}</span>
        {isOwner && (
          <button
            className="ai-chat-dashboard-title-edit-btn"
            onClick={() => setEditing(true)}
            aria-label="Rename dashboard"
            title="Rename dashboard"
          >✏️</button>
        )}
      </span>
    );
  }

  return (
    <span className="ai-chat-dashboard-title-wrapper">
      <input
        ref={inputRef}
        className="ai-chat-dashboard-title-input"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        disabled={saving}
        maxLength={120}
      />
      {saving && <Spinner size="small" />}
    </span>
  );
}

// Same toggle behavior/state as the shared ThemeToggle.jsx (used
// everywhere else in the toolkit) — reads/writes the same "smartbiz-theme"
// localStorage key and the same data-theme attribute + awsui-dark-mode
// class on <html>/<body>, so switching it here or on Home.jsx affects the
// same app-wide preference. Rendered as one of this page's own dark-
// masthead pill buttons instead of reusing ThemeToggle's Cloudscape
// Toggle-switch-in-a-floated-Box markup, which wouldn't fit this page's
// button-row visual language.
function DashboardThemeToggleButton() {
  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem('smartbiz-theme');
    if (stored) return stored === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    if (isDark) {
      root.setAttribute('data-theme', 'dark');
      body.classList.add('awsui-dark-mode');
    } else {
      root.setAttribute('data-theme', 'light');
      body.classList.remove('awsui-dark-mode');
    }
    localStorage.setItem('smartbiz-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  return (
    <button
      className="ai-chat-dashboard-btn ai-chat-dashboard-btn-ghost"
      onClick={() => setIsDark((d) => !d)}
      aria-label="Toggle light/dark theme"
    >
      {isDark ? '🌙 Dark' : '☀️ Light'}
    </button>
  );
}

export default function DashboardPage() {
  const { dashboardId } = useParams();
  const [dashboard, setDashboard] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [filters, setFilters] = useState({ channel: null, shop_id: null, start_date: null, end_date: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shares, setShares] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await dashboardsApi.getDashboard(dashboardId);
      setDashboard(data);
      setFilters(data.filters || { channel: null, shop_id: null, start_date: null, end_date: null });
      // Widgets from GET have no cached row data (see routes/dashboards.py
      // — the detail route returns the saved spec, not query results) —
      // trigger an immediate refresh so the page shows real data on load,
      // not just empty widget shells.
      const refreshed = await dashboardsApi.refreshDashboard(dashboardId, data.filters || {});
      setWidgets(refreshed.widgets || []);
    } catch (e) {
      setError(e.message || 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  }, [dashboardId]);

  useEffect(() => { load(); }, [load]);

  const handleApplyFilters = async () => {
    setRefreshing(true);
    setError('');
    try {
      const refreshed = await dashboardsApi.refreshDashboard(dashboardId, filters);
      setWidgets(refreshed.widgets || []);
      setDashboard((d) => ({ ...d, last_refreshed_at: refreshed.last_refreshed_at }));
    } catch (e) {
      setError(e.message || 'Refresh failed.');
    } finally {
      setRefreshing(false);
    }
  };

  const openShareModal = async () => {
    try {
      const data = await dashboardsApi.listShares(dashboardId);
      setShares(data.shares || []);
      setShareModalOpen(true);
    } catch (e) {
      setError(e.message || 'Failed to load shares.');
    }
  };

  const handleDelete = async () => {
    try {
      await dashboardsApi.deleteDashboard(dashboardId);
      window.close();
      // If window.close() is blocked (not opened via script), fall back
      // to navigating to the chat.
      window.location.href = '/ai-chat';
    } catch (e) {
      setError(e.message || 'Failed to delete dashboard.');
    }
  };

  const handleDuplicate = async () => {
    const confirmed = window.confirm(
      `Create a copy of "${dashboard.title}"? The new dashboard will be owned by you.`
    );
    if (!confirmed) return;
    try {
      const newDashboard = await dashboardsApi.createDashboard({
        title: `${dashboard.title} (Copy)`,
        widgets: (dashboard.widgets || []).map((w) => ({ ...w })),
        filters: filters,
      });
      window.open(`/ai-chat/dashboard/${newDashboard.dashboard_id}`, '_blank');
    } catch (e) {
      setError(e.message || 'Failed to duplicate dashboard.');
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" /> Loading dashboard…
      </Box>
    );
  }

  if (error && !dashboard) {
    return (
      <Box padding="l">
        <Alert type="error" header="Couldn't load this dashboard">{error}</Alert>
        <Box padding={{ top: 'm' }}><Link to="/ai-chat">← Back to AI Chat</Link></Box>
      </Box>
    );
  }

  const isOwner = dashboard?.is_owner;

  return (
    <>
    <AppLayout
      navigationHide
      toolsHide
      disableContentPaddings
      content={
        <div className="ai-chat-dashboard-page">
          {/* Custom dark masthead — replaces Cloudscape's default <Header>,
              matching the reference sample HTML's topbar (brand mark +
              wordmark + title + owner/refreshed meta + action buttons).
              Scoped to this one page via ai-chat.css's
              .ai-chat-dashboard-topbar rules; no other page in the toolkit
              is affected. */}
          <header className="ai-chat-dashboard-topbar">
            <div className="ai-chat-dashboard-topbar-inner">
              <div className="ai-chat-dashboard-brand">
                <div className="ai-chat-dashboard-brand-mark">S</div>
                <div className="ai-chat-dashboard-brand-text">
                  <div className="name">SmartBiz Insights</div>
                  <div className="sub">Analytics</div>
                </div>
              </div>
              <h1 className="ai-chat-dashboard-topbar-title">
                <EditableTitle
                  title={dashboard?.title || ''}
                  isOwner={isOwner}
                  onSave={async (newTitle) => {
                    await dashboardsApi.updateDashboard(dashboardId, { title: newTitle });
                    setDashboard((d) => ({ ...d, title: newTitle }));
                  }}
                />
              </h1>
              <div className="ai-chat-dashboard-topbar-meta">
                <div className="ai-chat-dashboard-topbar-meta-item">
                  Owner<b>{dashboard?.owner_username}</b>
                </div>
                <div className="ai-chat-dashboard-topbar-meta-item">
                  Last refreshed
                  <b>
                    {dashboard?.last_refreshed_at
                      ? new Date(dashboard.last_refreshed_at).toLocaleString()
                      : 'never'}
                  </b>
                </div>
                <div className="ai-chat-dashboard-topbar-actions">
                  <DashboardThemeToggleButton />
                  <button className="ai-chat-dashboard-btn ai-chat-dashboard-btn-ghost" onClick={() => window.print()}>
                    Export PDF
                  </button>
                  <button className="ai-chat-dashboard-btn ai-chat-dashboard-btn-ghost" onClick={handleDuplicate}>
                    Duplicate
                  </button>
                  {isOwner && (
                    <button className="ai-chat-dashboard-btn ai-chat-dashboard-btn-solid" onClick={openShareModal}>
                      Share
                    </button>
                  )}
                  {isOwner && (
                    <button className="ai-chat-dashboard-btn ai-chat-dashboard-btn-danger" onClick={() => setDeleteConfirm(true)}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          </header>

          {/* Scope bar — the functional filter controls, styled as a dark
              strip continuing the masthead rather than a plain white row. */}
          <div className="ai-chat-dashboard-scopebar">
            <div className="ai-chat-dashboard-scopebar-inner">
              <span className="ai-chat-dashboard-scopebar-label">Report scope</span>
              <DashboardFilterBar
                filters={filters}
                onChange={setFilters}
                onApply={handleApplyFilters}
                applying={refreshing}
              />
            </div>
          </div>

          <Box padding="l">
            {error && (
              <Box padding={{ bottom: 'l' }}>
                <Alert type="error" dismissible onDismiss={() => setError('')}>{error}</Alert>
              </Box>
            )}

            <div className="ai-chat-dashboard-body">
              <DashboardSections widgets={widgets} />
            </div>

            {widgets.length === 0 && (
              <Box textAlign="center" color="text-status-inactive" padding="xl">
                This dashboard has no widgets.
              </Box>
            )}
          </Box>
        </div>
      }
    />
    {shareModalOpen && (
      <ShareDashboardModal
        dashboardId={dashboardId}
        shares={shares}
        onDismiss={() => setShareModalOpen(false)}
        onShared={openShareModal}
        onRevoked={openShareModal}
      />
    )}
    {deleteConfirm && (
      <Modal
        visible={true}
        onDismiss={() => setDeleteConfirm(false)}
        header="Delete dashboard"
        footer={
          <Box float="right">
            <Button variant="link" onClick={() => setDeleteConfirm(false)}>Cancel</Button>{' '}
            <Button variant="primary" onClick={handleDelete}>Yes, delete</Button>
          </Box>
        }
      >
        Are you sure you want to delete <strong>{dashboard?.title}</strong>? This cannot be undone.
      </Modal>
    )}
    </>
  );
}
